import { isInIframe } from "common/utils/dom";
import { useIsHdEnabled } from "common/contexts/UIContext";

import { useSupportChat } from "client/support/hooks";
import StreamlinedHeader from "client/common/components/header/StreamlinedHeader";
import Page from "client/common/components/layouts/Page";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import ErrorContainer from "client/common/components/errors/ErrorContainer";
import Alert from "client/common/components/content/Alert";

import { AppointmentBookingStep } from "../types";
import { BOOKING_STEPS } from "../constants/booking";

import styles from "../styles/AppointmentBookingPage.module.less";

type AppointmentBookingHeaderProps = {
  step: AppointmentBookingStep;
  onPrevious: () => void;
};

const AppointmentBookingHeader = ({ step, onPrevious }: AppointmentBookingHeaderProps) => {
  const chat = useSupportChat();
  const isHdEnabled = useIsHdEnabled();

  return (
    <StreamlinedHeader
      // Hide back if we're on the first step in an iframe or if HD is disabled
      hasPrevious={step !== AppointmentBookingStep.Start || (!isInIframe() && isHdEnabled)}
      onPrevious={onPrevious}
      hasLogo={!isInIframe()}
      // use this area to show a step count once we're past start
      onHelp={step === AppointmentBookingStep.Start ? () => chat.show() : () => null}
      renderHelp={() => (
        <div className={styles.count}>
          {BOOKING_STEPS.indexOf(step) + 1}{" "}
          <span className={styles.outOf}>/ {BOOKING_STEPS.length}</span>
        </div>
      )}
    />
  );
};

export type AppointmentBookingPageProps = {
  content?: React.ReactNode;
  button?: React.ReactNode;
  error?: string;
  step: AppointmentBookingStep;
  onPrevious: () => void;
};

const AppointmentBookingPage = ({
  content,
  button,
  error,
  step,
  onPrevious,
}: AppointmentBookingPageProps) => {
  return (
    <Page
      Header={<AppointmentBookingHeader step={step} onPrevious={onPrevious} />}
      Footer={null}
      className={styles.page}
    >
      <div className={styles.content}>
        {error && <Alert variant="danger">{error}</Alert>}
        {content}
        {button}
      </div>
    </Page>
  );
};

const AppointmentBookingLoading = (props: Pick<AppointmentBookingPageProps, "onPrevious">) => {
  return (
    <AppointmentBookingPage
      {...props}
      step={AppointmentBookingStep.Start}
      content={<LoadingContainer />}
    />
  );
};

type AppointmentBookingErrorProps = Pick<AppointmentBookingPageProps, "onPrevious"> & {
  description?: string;
};

const AppointmentBookingError = ({ description, onPrevious }: AppointmentBookingErrorProps) => {
  return (
    <AppointmentBookingPage
      onPrevious={onPrevious}
      step={AppointmentBookingStep.Start}
      content={<ErrorContainer description={description} />}
    />
  );
};

export default Object.assign(AppointmentBookingPage, {
  Loading: AppointmentBookingLoading,
  Error: AppointmentBookingError,
});
